import type { Grade, Problem, Token, Val } from "../types";
import type { Rng } from "../rng";
import type { Skill } from "../game/config";

function gcd(a: number, b: number): number {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) {
    const t = a % b;
    a = b;
    b = t;
  }
  return a || 1;
}

function reduce(n: number, d: number): [number, number] {
  if (d < 0) {
    n = -n;
    d = -d;
  }
  const g = gcd(n, d);
  return [n / g, d / g];
}

export function intVal(n: number): Val {
  n = Math.trunc(n);
  return {
    kind: "int",
    n,
    compact: String(n),
    speak: n < 0 ? `negative ${Math.abs(n)}` : String(n),
  };
}

export function fracVal(n: number, d: number, unreduced = false): Val {
  if (!unreduced) {
    [n, d] = reduce(n, d);
  }
  const compact = d === 1 ? String(n) : `${n}/${d}`;
  return {
    kind: d === 1 ? "int" : "frac",
    n,
    d,
    compact,
    speak: d === 1 ? String(n) : `${n} over ${d}`,
  };
}

export function decVal(n: number, places = 2): Val {
  const f = Math.round(n * 10 ** places) / 10 ** places;
  const compact = f.toFixed(places).replace(/\.?0+$/, (m) => (m.includes(".") ? m.replace(/0+$/, "").replace(/\.$/, "") : m));
  const shown = Math.abs(f - Math.round(f)) < 1e-9 ? String(Math.round(f)) : f.toFixed(places);
  return {
    kind: "dec",
    n: f,
    places,
    compact: shown,
    speak: shown.replace(".", " point "),
  };
}

export function remVal(q: number, r: number, d: number): Val {
  return {
    kind: "rem",
    n: q,
    q,
    r,
    d,
    compact: `${q} R${r}`,
    speak: `${q} remainder ${r}`,
  };
}

export function valEq(a: Val, b: Val): boolean {
  if (a.kind === "rem" || b.kind === "rem") {
    return a.kind === "rem" && b.kind === "rem" && a.q === b.q && a.r === b.r;
  }
  const ar = toRat(a);
  const br = toRat(b);
  return ar[0] * br[1] === ar[1] * br[0];
}

function toRat(v: Val): [number, number] {
  if (v.kind === "frac") return [v.n, v.d || 1];
  if (v.kind === "dec") {
    const p = v.places || 2;
    return [Math.round(v.n * 10 ** p), 10 ** p];
  }
  return [v.n, 1];
}

function tokN(v: number): Token {
  return { t: "n", v };
}
function tokOp(v: string): Token {
  return { t: "op", v };
}
function tokF(n: number, d: number): Token {
  return { t: "frac", n, d };
}

function blankEq(tokens: Token[], answer: Val, kind: string, speak: string, hint: string, hintKind: Problem["hintKind"]): Problem {
  return {
    id: kind,
    tokens: [...tokens, tokOp("="), { t: "blank" }],
    speak: `${speak} equals what?`,
    answer,
    kind,
    hint,
    hintKind,
  };
}

function addHint(a: number, b: number): string {
  if (a + b <= 10) return `Think of a number line: start at ${a}, hop +${b}.`;
  return `Add the ones, then the tens.`;
}

function subHint(a: number, b: number): string {
  return `Think of a number line: start at ${a}, hop back ${b}.`;
}

function mulHint(a: number, b: number): string {
  return `Think of ${a} equal groups of ${b}.`;
}

function familyHint(a: number, b: number, op: string): string {
  if (op === "+") return `Fact family: ${a} + ${b} = ${a + b}, so ${a + b} − ${a} = ${b}.`;
  if (op === "×") return `Fact family: ${a} × ${b} = ${a * b}, so ${a * b} ÷ ${a} = ${b}.`;
  return `Look at the related fact.`;
}

export function generateProblem(skills: Skill[], grade: Grade, rng: Rng): Problem {
  for (let attempt = 0; attempt < 12; attempt++) {
    const skill = rng.pick(skills);
    const p = build(skill, grade, rng);
    if (p) return p;
  }
  return build("add10", grade, rng) as Problem;
}

function build(skill: Skill, grade: Grade, rng: Rng): Problem | null {
  switch (skill) {
    case "add5":
      return addSub(rng, 0, 5, "+");
    case "sub5":
      return addSub(rng, 0, 5, "-");
    case "add10":
      return addSub(rng, 0, 10, "+");
    case "sub10":
      return addSub(rng, 0, 10, "-");
    case "add20":
      return addSub(rng, 0, 20, "+");
    case "sub20":
      return addSub(rng, 0, 20, "-");
    case "doubles": {
      const a = rng.intInc(1, grade <= 1 ? 10 : 12);
      return blankEq([tokN(a), tokOp("+"), tokN(a)], intVal(a + a), "doubles", `${a} plus ${a}`, `Doubles: ${a} + ${a} is two of the same.`, "line");
    }
    case "make10": {
      const a = rng.intInc(1, 9);
      const b = 10 - a;
      return blankEq([tokN(a), tokOp("+"), tokN(b)], intVal(10), "make10", `${a} plus ${b}`, `These partners make 10.`, "family");
    }
    case "add2d":
      return twoDigit(rng, "+", false);
    case "add2dR":
      return twoDigit(rng, "+", true);
    case "sub2d":
      return twoDigit(rng, "-", false);
    case "sub2dR":
      return twoDigit(rng, "-", true);
    case "mul10":
      return mulDiv(rng, 0, 10, "×");
    case "div10":
      return mulDiv(rng, 1, 10, "÷");
    case "add100":
      return within(rng, 100, "+");
    case "sub100":
      return within(rng, 100, "-");
    case "unitFrac":
      return unitFrac(rng);
    case "mul12":
      return mulDiv(rng, 0, 12, "×");
    case "div12":
      return mulDiv(rng, 1, 12, "÷");
    case "mulMd":
      return mulMd(rng, grade >= 4);
    case "eqFrac":
      return eqFracFixed(rng);
    case "fracLike":
      return fracLike(rng, "+");
    case "mulBig":
      return mulBig(rng);
    case "divShort":
      return divShort(rng);
    case "fracUnlike":
      return fracUnlike(rng);
    case "fracTimesW":
      return fracTimesW(rng);
    case "fracDiv":
      return fracDiv(rng);
    case "decAdd":
      return decAdd(rng);
    case "ratio":
      return ratio(rng);
    case "intAdd":
      return intAdd(rng);
    case "intMul":
      return intMul(rng);
    case "rational":
      return rational(rng);
    case "order":
      return orderOps(rng);
    case "exp":
      return expProb(rng);
    case "sqrt":
      return sqrtProb(rng);
    default:
      return addSub(rng, 0, 10, "+");
  }
}

function addSub(rng: Rng, lo: number, hi: number, op: "+" | "-"): Problem {
  if (op === "+") {
    const a = rng.intInc(lo, hi);
    const b = rng.intInc(lo, hi - a);
    return blankEq(
      [tokN(a), tokOp("+"), tokN(b)],
      intVal(a + b),
      "add",
      `${a} plus ${b}`,
      addHint(a, b),
      "line"
    );
  }
  const a = rng.intInc(lo, hi);
  const b = rng.intInc(lo, a);
  return blankEq([tokN(a), tokOp("−"), tokN(b)], intVal(a - b), "sub", `${a} minus ${b}`, subHint(a, b), "line");
}

function twoDigit(rng: Rng, op: "+" | "-", regroup: boolean): Problem {
  if (op === "+") {
    const aT = rng.intInc(1, 8);
    const bT = rng.intInc(1, 9 - aT);
    let aO: number, bO: number;
    if (regroup) {
      aO = rng.intInc(1, 9);
      bO = rng.intInc(10 - aO, 9);
    } else {
      aO = rng.intInc(0, 8);
      bO = rng.intInc(0, 9 - aO);
    }
    const a = aT * 10 + aO;
    const b = bT * 10 + bO;
    return blankEq(
      [tokN(a), tokOp("+"), tokN(b)],
      intVal(a + b),
      regroup ? "add-re" : "add-2d",
      `${a} plus ${b}`,
      regroup ? `Ones will make a ten — regroup.` : `Add ones, then tens. No regrouping.`,
      "line"
    );
  }
  let a: number, b: number;
  if (regroup) {
    const aT = rng.intInc(2, 9);
    const aO = rng.intInc(0, 8);
    const bT = rng.intInc(1, aT - 1);
    const bO = rng.intInc(aO + 1, 9);
    a = aT * 10 + aO;
    b = bT * 10 + bO;
  } else {
    const aT = rng.intInc(2, 9);
    const aO = rng.intInc(1, 9);
    const bT = rng.intInc(1, aT);
    const bO = rng.intInc(0, aO);
    a = aT * 10 + aO;
    b = bT * 10 + bO;
    if (a === b) a += 10;
    if (b > a) return twoDigit(rng, op, regroup);
  }
  return blankEq(
    [tokN(a), tokOp("−"), tokN(b)],
    intVal(a - b),
    regroup ? "sub-re" : "sub-2d",
    `${a} minus ${b}`,
    regroup ? `You may need to regroup a ten.` : `Subtract ones, then tens.`,
    "line"
  );
}

function within(rng: Rng, hi: number, op: "+" | "-"): Problem {
  if (op === "+") {
    const a = rng.intInc(10, hi - 5);
    const b = rng.intInc(1, Math.min(40, hi - a));
    return blankEq([tokN(a), tokOp("+"), tokN(b)], intVal(a + b), "add100", `${a} plus ${b}`, addHint(a, b), "line");
  }
  const a = rng.intInc(20, hi);
  const b = rng.intInc(1, a);
  return blankEq([tokN(a), tokOp("−"), tokN(b)], intVal(a - b), "sub100", `${a} minus ${b}`, subHint(a, b), "line");
}

function mulDiv(rng: Rng, lo: number, hi: number, op: "×" | "÷"): Problem {
  if (op === "×") {
    const a = rng.chance(0.12) ? rng.intInc(lo, hi) : rng.intInc(Math.max(lo, 2), hi);
    const b = rng.chance(0.12) ? rng.intInc(lo, hi) : rng.intInc(Math.max(1, lo), hi);
    return blankEq(
      [tokN(a), tokOp("×"), tokN(b)],
      intVal(a * b),
      "mul",
      `${a} times ${b}`,
      mulHint(a, b),
      "groups"
    );
  }
  const b = rng.intInc(Math.max(1, lo), hi);
  const q = rng.intInc(0, hi);
  const a = b * q;
  return blankEq(
    [tokN(a), tokOp("÷"), tokN(b)],
    intVal(q),
    "div",
    `${a} divided by ${b}`,
    familyHint(b, q, "×"),
    "family"
  );
}

function mulMd(rng: Rng, three: boolean): Problem {
  const a = three && rng.chance(0.35) ? rng.intInc(100, 399) : rng.intInc(12, 99);
  const b = rng.intInc(2, 9);
  return blankEq([tokN(a), tokOp("×"), tokN(b)], intVal(a * b), "mul-md", `${a} times ${b}`, mulHint(a, b), "groups");
}

function mulBig(rng: Rng): Problem {
  const a = rng.intInc(11, 29);
  const b = rng.intInc(11, 19);
  return blankEq([tokN(a), tokOp("×"), tokN(b)], intVal(a * b), "mul-big", `${a} times ${b}`, `Break it up: (${a} × 10) + (${a} × ${b - 10}).`, "groups");
}

function divShort(rng: Rng): Problem {
  const d = rng.intInc(2, 9);
  const q = rng.intInc(11, 48);
  const a = d * q;
  return blankEq([tokN(a), tokOp("÷"), tokN(d)], intVal(q), "div-short", `${a} divided by ${d}`, `How many groups of ${d} fit in ${a}?`, "groups");
}

function unitFrac(rng: Rng): Problem {
  if (rng.chance(0.5)) {
    const d = rng.pick([2, 3, 4]);
    const n = rng.intInc(1, d);
    if (n === d) {
      return blankEq([tokN(n), tokOp("×"), tokF(1, d)], intVal(1), "unit-n", `${n} times 1 over ${d}`, `${n} copies of 1/${d} make a whole.`, "groups");
    }
    return blankEq(
      [tokN(n), tokOp("×"), tokF(1, d)],
      fracVal(n, d),
      "unit-n",
      `${n} times 1 over ${d}`,
      `${n} copies of 1/${d}.`,
      "groups"
    );
  }
  const d = rng.pick([2, 3, 4, 4]);
  return blankEq(
    [tokF(1, d), tokOp("+"), tokF(1, d)],
    fracVal(2, d),
    "unit-add",
    `1 over ${d} plus 1 over ${d}`,
    `Same-size pieces: add the counts.`,
    "family"
  );
}

function eqFracFixed(rng: Rng): Problem {
  const a = rng.intInc(1, 5);
  const d = rng.pick([2, 3, 4, 5, 6]);
  if (gcd(a, d) === d) return eqFracFixed(rng);
  const k = rng.pick([2, 3, 4]);
  const den = d * k;
  const n = a * k;
  return {
    id: "eq-frac",
    tokens: [
      tokF(a, d),
      tokOp("="),
      { t: "text", v: "?" },
      { t: "text", v: `/${den}` },
    ],
    speak: `${a} over ${d} equals what over ${den}?`,
    answer: intVal(n),
    kind: "eq-frac",
    hint: `Multiply top and bottom by ${k}. ${a} × ${k} = ${n}.`,
    hintKind: "family",
  };
}

function fracLike(rng: Rng, op: "+" | "−"): Problem {
  const d = rng.pick([3, 4, 5, 6, 8, 10]);
  const a = rng.intInc(1, d - 1);
  const b = rng.intInc(1, d - a);
  if (op === "+") {
    return blankEq(
      [tokF(a, d), tokOp("+"), tokF(b, d)],
      fracVal(a + b, d),
      "frac-like",
      `${a} over ${d} plus ${b} over ${d}`,
      `Same denominators: add the numerators.`,
      "family"
    );
  }
  const x = Math.max(a, b);
  const y = Math.min(a, b);
  return blankEq(
    [tokF(x, d), tokOp("−"), tokF(y, d)],
    fracVal(x - y, d),
    "frac-like",
    `${x} over ${d} minus ${y} over ${d}`,
    `Same denominators: subtract the numerators.`,
    "family"
  );
}

function fracUnlike(rng: Rng): Problem {
  const pairs: [number, number][] = [
    [2, 4],
    [2, 8],
    [4, 8],
    [3, 6],
    [3, 12],
    [6, 12],
    [5, 10],
  ];
  const [d1, d2] = rng.pick(pairs);
  const n1 = rng.intInc(1, d1 - 1);
  const n2 = rng.intInc(1, Math.min(d2 - 1, 3));
  const lcd = (d1 * d2) / gcd(d1, d2);
  const sumN = n1 * (lcd / d1) + n2 * (lcd / d2);
  return blankEq(
    [tokF(n1, d1), tokOp("+"), tokF(n2, d2)],
    fracVal(sumN, lcd),
    "frac-unlike",
    `${n1} over ${d1} plus ${n2} over ${d2}`,
    `Use a common denominator of ${lcd}.`,
    "family"
  );
}

function fracTimesW(rng: Rng): Problem {
  const d = rng.pick([2, 3, 4, 5, 6, 8]);
  const n = rng.intInc(1, d - 1);
  const w = rng.intInc(2, 8);
  const [nn, dd] = reduce(w * n, d);
  return blankEq(
    [tokN(w), tokOp("×"), tokF(n, d)],
    dd === 1 ? intVal(nn) : fracVal(nn, dd),
    "frac-w",
    `${w} times ${n} over ${d}`,
    `${w} groups of ${n}/${d}.`,
    "groups"
  );
}

function fracDiv(rng: Rng): Problem {
  if (rng.chance(0.5)) {
    const d = rng.pick([2, 3, 4, 5]);
    const n = rng.intInc(1, 4) * d;
    const a = n / d;
    const whole = rng.intInc(2, 9);
    // whole ÷ (1/d) = whole * d
    if (rng.chance(0.5)) {
      const dd = rng.pick([2, 3, 4]);
      return blankEq(
        [tokN(whole), tokOp("÷"), tokF(1, dd)],
        intVal(whole * dd),
        "w-div-f",
        `${whole} divided by 1 over ${dd}`,
        `How many ${1}/${dd}s fit in ${whole}?`,
        "groups"
      );
    }
    const num = rng.intInc(1, 3);
    const den = rng.pick([2, 3, 4]);
    if (num >= den) return fracDiv(rng);
    const k = rng.intInc(1, 4);
    const leftN = num * k;
    const leftD = den;
    // (k*num)/den ÷ num/den = k
    return blankEq(
      [tokF(leftN, leftD), tokOp("÷"), tokF(num, den)],
      intVal(k),
      "f-div-f",
      `${leftN} over ${leftD} divided by ${num} over ${den}`,
      `How many ${num}/${den} fit in ${leftN}/${leftD}?`,
      "groups"
    );
  }
  const den = rng.pick([2, 3, 4]);
  const k = rng.intInc(2, 6);
  return blankEq(
    [tokF(k, den), tokOp("÷"), tokF(1, den)],
    intVal(k),
    "f-div-u",
    `${k} over ${den} divided by 1 over ${den}`,
    `Pieces of the same size: ${k} of them.`,
    "groups"
  );
}

function decAdd(rng: Rng): Problem {
  const a = rng.intInc(5, 80);
  const b = rng.intInc(5, 80);
  const op = rng.chance(0.7) ? "+" : "−";
  if (op === "+") {
    const av = a / 100;
    const bv = b / 100;
    return blankEq(
      [{ t: "text", v: av.toFixed(2) }, tokOp("+"), { t: "text", v: bv.toFixed(2) }],
      decVal(av + bv, 2),
      "dec-add",
      `${av.toFixed(2)} plus ${bv.toFixed(2)}`,
      `Line up the hundredths.`,
      "line"
    );
  }
  const hi = Math.max(a, b);
  const lo = Math.min(a, b);
  const av = hi / 100;
  const bv = lo / 100;
  return blankEq(
    [{ t: "text", v: av.toFixed(2) }, tokOp("−"), { t: "text", v: bv.toFixed(2) }],
    decVal(av - bv, 2),
    "dec-sub",
    `${av.toFixed(2)} minus ${bv.toFixed(2)}`,
    `Line up the hundredths.`,
    "line"
  );
}

function ratio(rng: Rng): Problem {
  const a = rng.intInc(1, 5);
  let b = rng.intInc(1, 6);
  if (a === b) b = a + 1;
  const parts = a + b;
  const k = rng.intInc(2, 8);
  const whole = parts * k;
  const askA = rng.chance(0.5);
  const ans = askA ? a * k : b * k;
  return blankEq(
    [
      { t: "text", v: `${a}:${b}` },
      { t: "text", v: " of " },
      tokN(whole),
      { t: "text", v: askA ? " → first" : " → second" },
    ],
    intVal(ans),
    "ratio",
    `${a} to ${b} of ${whole}, ${askA ? "first" : "second"} part`,
    `${parts} equal parts of ${whole} are ${k} each.`,
    "groups"
  );
}

function intAdd(rng: Rng): Problem {
  const a = rng.intInc(-12, 12);
  const b = rng.intInc(-12, 12);
  if (a === 0 || b === 0) return intAdd(rng);
  const op = rng.chance(0.65) ? "+" : "−";
  const res = op === "+" ? a + b : a - b;
  return blankEq(
    [tokN(a), tokOp(op === "+" ? "+" : "−"), tokN(b)],
    intVal(res),
    "int",
    `${a} ${op === "+" ? "plus" : "minus"} ${b}`,
    op === "+" ? `Move ${b} on the number line from ${a}.` : `Start at ${a}, move ${b < 0 ? "positive" : "negative"} direction.`,
    "sign"
  );
}

function intMul(rng: Rng): Problem {
  const a = rng.pick([-9, -8, -6, -5, -4, -3, -2, 2, 3, 4, 5, 6, 8]);
  const b = rng.pick([-8, -6, -3, -2, 2, 3, 4, 5, 6, 7]);
  return blankEq(
    [tokN(a), tokOp("×"), tokN(b)],
    intVal(a * b),
    "int-mul",
    `${a} times ${b}`,
    a < 0 && b < 0 ? `Negative times negative is positive.` : a < 0 || b < 0 ? `One negative factor makes a negative product.` : mulHint(a, b),
    "sign"
  );
}

function rational(rng: Rng): Problem {
  if (rng.chance(0.5)) {
    const d = rng.pick([2, 4]);
    const a = rng.pick([1, 1, 3]);
    const b = rng.pick([-1, -3, 1]);
    const d2 = rng.pick([2, 4]);
    const lcd = (d * d2) / gcd(d, d2);
    const n = a * (lcd / d) + b * (lcd / d2);
    return blankEq(
      [tokF(a, d), tokOp("+"), tokF(b, d2)],
      fracVal(n, lcd),
      "rat-frac",
      `${a} over ${d} plus ${b} over ${d2}`,
      `Common denominator ${lcd}, watch the sign.`,
      "sign"
    );
  }
  return intAdd(rng);
}

function orderOps(rng: Rng): Problem {
  const a = rng.intInc(1, 9);
  const b = rng.intInc(2, 9);
  const c = rng.intInc(2, 8);
  if (rng.chance(0.5)) {
    const ans = a + b * c;
    return blankEq(
      [tokN(a), tokOp("+"), tokN(b), tokOp("×"), tokN(c)],
      intVal(ans),
      "order",
      `${a} plus ${b} times ${c}`,
      `Multiply first: ${b} × ${c} = ${b * c}, then add ${a}.`,
      "order"
    );
  }
  const ans = a * b + c;
  return blankEq(
    [tokN(a), tokOp("×"), tokN(b), tokOp("+"), tokN(c)],
    intVal(ans),
    "order",
    `${a} times ${b} plus ${c}`,
    `Multiply first, then add.`,
    "order"
  );
}

function expProb(rng: Rng): Problem {
  if (rng.chance(0.45)) {
    const base = rng.intInc(2, 8);
    const ans = base * base;
    return blankEq(
      [{ t: "sup", base: String(base), exp: "2" }],
      intVal(ans),
      "sq",
      `${base} squared`,
      `${base} × ${base}.`,
      "groups"
    );
  }
  if (rng.chance(0.5)) {
    const base = rng.intInc(2, 5);
    const ans = base * base * base;
    return blankEq(
      [{ t: "sup", base: String(base), exp: "3" }],
      intVal(ans),
      "cube",
      `${base} cubed`,
      `${base} × ${base} × ${base}.`,
      "groups"
    );
  }
  const base = rng.intInc(3, 9);
  const sub = rng.intInc(1, 20);
  return blankEq(
    [{ t: "sup", base: String(base), exp: "2" }, tokOp("−"), tokN(sub)],
    intVal(base * base - sub),
    "sq-sub",
    `${base} squared minus ${sub}`,
    `Square first: ${base} × ${base} = ${base * base}.`,
    "order"
  );
}

function sqrtProb(rng: Rng): Problem {
  const r = rng.pick([4, 5, 6, 7, 8, 9, 10, 11, 12]);
  return blankEq([{ t: "sqrt", v: r * r }], intVal(r), "sqrt", `square root of ${r * r}`, `Which number times itself is ${r * r}?`, "groups");
}

export function generateProblemSafe(skills: Skill[], grade: Grade, rng: Rng): Problem {
  const mapped = skills.map((s) => (s === "eqFrac" ? "eqFrac" : s));
  for (let i = 0; i < 16; i++) {
    const skill = rng.pick(mapped);
    const p = skill === "eqFrac" ? eqFracFixed(rng) : build(skill, grade, rng);
    if (p && Number.isFinite(toRat(p.answer)[0])) return p;
  }
  return addSub(rng, 0, 10, "+");
}

export function tokensToPlain(tokens: Token[]): string {
  return tokens
    .map((t) => {
      if (t.t === "n") return String(t.v);
      if (t.t === "text") return t.v;
      if (t.t === "frac") return `${t.n}/${t.d}`;
      if (t.t === "op") return ` ${t.v} `;
      if (t.t === "sup") return `${t.base}^${t.exp}`;
      if (t.t === "sqrt") return `√${t.v}`;
      if (t.t === "blank") return "?";
      return "";
    })
    .join("")
    .replace(/\s+/g, " ")
    .trim();
}

export function renderTokensHtml(tokens: Token[], stacked = true): string {
  return tokens
    .map((t) => {
      if (t.t === "n") return `<span class="tn">${formatNum(t.v)}</span>`;
      if (t.t === "text") return `<span class="tt">${esc(t.v)}</span>`;
      if (t.t === "op") return `<span class="to">${esc(t.v)}</span>`;
      if (t.t === "blank") return `<span class="blank">?</span>`;
      if (t.t === "sup") return `<span class="tsup">${esc(t.base)}<sup>${esc(t.exp)}</sup></span>`;
      if (t.t === "sqrt") return `<span class="tsqrt"><span class="rad">√</span><span class="radic">${t.v}</span></span>`;
      if (t.t === "frac") {
        if (!stacked) return `<span class="tn">${t.n}/${t.d}</span>`;
        return `<span class="frac"><span class="num">${formatNum(t.n)}</span><span class="den">${t.d}</span></span>`;
      }
      return "";
    })
    .join("");
}

function formatNum(n: number): string {
  if (!Number.isFinite(n)) return "?";
  return String(n).replace("-", "−");
}

function esc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

export function distractors(p: Problem, rng: Rng): Val[] {
  const ans = p.answer;
  const cands: Val[] = [];
  const add = (v: Val | null) => {
    if (!v) return;
    if (valEq(v, ans)) return;
    if (cands.some((c) => valEq(c, v))) return;
    if (v.kind === "int" && Math.abs(v.n) > 40000) return;
    const signed = p.kind.startsWith("int") || p.kind === "rational" || p.kind === "rat-frac";
    if (v.kind === "int" && v.n < 0 && !signed) return;
    cands.push(v);
  };

  const n = ans.kind === "int" ? ans.n : ans.kind === "dec" ? ans.n : ans.n;
  add(intVal(Math.round(n) + 1));
  add(intVal(Math.round(n) - 1));
  add(intVal(Math.round(n) + 2));
  add(intVal(Math.round(n) - 2));

  const nums = p.tokens.filter((t) => t.t === "n") as { t: "n"; v: number }[];
  const fracs = p.tokens.filter((t) => t.t === "frac") as { t: "frac"; n: number; d: number }[];
  const ops = p.tokens.filter((t) => t.t === "op").map((t) => (t as { v: string }).v);

  if (nums.length >= 2) {
    const a = nums[0].v;
    const b = nums[1].v;
    add(intVal(a + b));
    add(intVal(a - b));
    add(intVal(b - a));
    add(intVal(a * b));
    if (b) add(intVal(Math.trunc(a / b)));
    add(intVal(a * (b + 1)));
    add(intVal((a + 1) * b));
    add(intVal(a * (b - 1)));
    add(intVal(a + b + 1));
    // forgot regroup: ones only for 2-digit add
    if (a >= 10 && b >= 10) {
      const ones = (a % 10) + (b % 10);
      const tens = Math.floor(a / 10) + Math.floor(b / 10);
      add(intVal(tens * 10 + (ones % 10))); // dropped carry
      add(intVal(a + (b % 10)));
    }
    add(intVal(-n));
    add(intVal(a + -b));
  }

  if (p.kind === "order" && nums.length >= 3) {
    const a = nums[0].v,
      b = nums[1].v,
      c = nums[2].v;
    add(intVal((a + b) * c));
    add(intVal(a + b + c));
    add(intVal(a * b * c));
    add(intVal(a + b * c + 1));
  }

  if (p.kind.startsWith("sq") || p.kind === "cube") {
    const m = p.tokens.find((t) => t.t === "sup") as { base: string } | undefined;
    if (m) {
      const b = Number(m.base);
      add(intVal(b * 2));
      add(intVal(b + 2));
      add(intVal(b * b + b));
    }
  }

  if (fracs.length >= 1) {
    const f = fracs[0];
    add(fracVal(f.n + 1, f.d, true));
    add(fracVal(f.n, f.d + 1, true));
    add(intVal(f.n + (fracs[1] ? fracs[1].n : 1)));
    if (fracs.length >= 2) {
      const g = fracs[1];
      add(fracVal(f.n + g.n, f.d + g.d, true)); // add dens
      add(fracVal(f.n + g.n, f.d, true)); // keep first den
      add(fracVal(f.n + g.n, g.d, true));
      add(intVal(f.n + g.n));
    }
  }

  if (ans.kind === "frac" && ans.d) {
    add(fracVal(ans.n + 1, ans.d, true));
    add(fracVal(ans.n, ans.d + 1, true));
    add(intVal(ans.n));
    add(fracVal(ans.n + ans.d, ans.d, true));
  }

  if (ans.kind === "dec") {
    add(decVal(ans.n + 0.1, 2));
    add(decVal(ans.n - 0.1, 2));
    add(decVal(ans.n + 0.01, 2));
    add(intVal(Math.round(ans.n)));
    add(decVal(ans.n * 10, 2));
  }

  if (ans.kind === "rem") {
    add(remVal((ans.q || 0) + 1, ans.r || 0, ans.d || 1));
    add(remVal(ans.q || 0, (ans.r || 0) + 1, ans.d || 1));
    add(intVal(ans.q || 0));
  }

  if (ops.includes("×") && nums.length >= 2) {
    add(intVal(nums[0].v + nums[1].v));
  }
  if (ops.includes("÷") && nums.length >= 2) {
    add(intVal(nums[0].v - nums[1].v));
    add(intVal(nums[0].v * nums[1].v));
  }

  // Fill with nearby ints
  let k = 1;
  while (cands.length < 8 && k < 20) {
    add(intVal(Math.round(n) + k));
    add(intVal(Math.round(n) - k));
    k++;
  }

  const out: Val[] = [];
  for (const c of rng.shuffle(cands)) {
    if (out.length >= 3) break;
    if (valEq(c, ans)) continue;
    if (out.some((o) => valEq(o, c))) continue;
    out.push(c);
  }
  while (out.length < 3) {
    const v = intVal(Math.round(n) + out.length + 3 + rng.int(4));
    if (!valEq(v, ans) && !out.some((o) => valEq(o, v))) out.push(v);
  }
  return out.slice(0, 3);
}

export function stonesFor(p: Problem, correctDir: Dir, rng: Rng, avoidDir?: Dir | null): Record<"N" | "E" | "S" | "W", Val> {
  const wrong = distractors(p, rng);
  const dirs: Array<"N" | "E" | "S" | "W"> = ["N", "E", "S", "W"];
  let place = correctDir;
  if (avoidDir && avoidDir === correctDir) {
    /* path forces this dir — allowed; path gen already tries to turn */
  }
  const rec = { N: wrong[0], E: wrong[1], S: wrong[2], W: wrong[0] } as Record<"N" | "E" | "S" | "W", Val>;
  const rest = rng.shuffle(dirs.filter((d) => d !== place));
  rec[place] = p.answer;
  rec[rest[0]] = wrong[0];
  rec[rest[1]] = wrong[1];
  rec[rest[2]] = wrong[2];
  return rec;
}
